import React, { useCallback, useMemo, useState } from 'react';
import { IconButton, Menu } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVert from '@mui/icons-material/MoreVert';
import { useI18n } from '../../../../lib/hook/useI18n';
import {
  SECTION_HEADER_SIDEBAR_KEBAB_MENU_ANCHOR_ORIGIN,
  SECTION_HEADER_SIDEBAR_KEBAB_MENU_TRANSFORM_ORIGIN,
  SectionHeaderMenuItem,
} from './SectionHeaderMenuShared';

type FolderMenuRowDef = {
  icon: React.ReactNode;
  key: string;
  messageKey: string;
  run: () => void;
  destructive?: boolean;
};

export interface EditableFolderKebabMenuProps {
  iconButtonSize: 'small' | 'medium' | 'large';
  onRename: () => void;
  onDelete?: () => void;
}

/** Kebab menu for folders (rename, delete options). */
const EditableFolderKebabMenu: React.FC<EditableFolderKebabMenuProps> = ({
  iconButtonSize,
  onRename,
  onDelete,
}) => {
  const { t } = useI18n();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent folder click/navigation
    setAnchorEl(e.currentTarget);
  }, []);

  const menuRows = useMemo<FolderMenuRowDef[]>(() => {
    const rows: FolderMenuRowDef[] = [];

    // Rename option
    rows.push({
      key: 'rename',
      icon: <Edit fontSize="small" />,
      messageKey: 'rooms.editor.folder.menu.rename',
      run: onRename,
    });

    // Delete option
    if (onDelete) {
      rows.push({
        key: 'delete',
        icon: <DeleteOutlineIcon fontSize="small" />,
        messageKey: 'rooms.editor.folder.menu.delete',
        run: onDelete,
        destructive: true,
      });
    }

    return rows;
  }, [onRename, onDelete]);

  return (
    <>
      <IconButton
        aria-haspopup="true"
        aria-label={t('rooms.editor.folder.menu.open_aria')}
        onClick={handleOpen}
        size={iconButtonSize}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s ease',
          '.folder-card:hover &': {
            opacity: 1,
          },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={SECTION_HEADER_SIDEBAR_KEBAB_MENU_ANCHOR_ORIGIN}
        open={open}
        onClose={handleClose}
        transformOrigin={SECTION_HEADER_SIDEBAR_KEBAB_MENU_TRANSFORM_ORIGIN}
      >
        {menuRows.map((row) => (
          <SectionHeaderMenuItem
            key={row.key}
            destructive={row.destructive}
            icon={row.icon}
            label={t(row.messageKey)}
            onClick={() => {
              handleClose();
              row.run();
            }}
          />
        ))}
      </Menu>
    </>
  );
};

export default EditableFolderKebabMenu;
